import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, MapPin } from "lucide-react";
import { client } from "../sanity";

const EVENTS_PER_PAGE = 9;

const formatEventDate = (value) => {
  if (!value) {
    return {
      full: "Date to be announced",
      short: "TBA",
    };
  }

  const date = new Date(value);

  return {
    full: date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    }),
    short: date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    }),
  };
};

function EventCard({ event }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const eventDate = formatEventDate(event.eventDate);
  const eventMessage =
    event.interestMessage ||
    `Hello Jisedai, I would like to register interest in ${event.title} taking place on ${eventDate.full} at ${event.location}. Please share more details.`;

  return (
    <article className="group overflow-hidden rounded-2xl border border-[#111712]/15 bg-[#f7f7f2] transition duration-500 hover:-translate-y-1 hover:shadow-xl hover:shadow-[#45695a]/15">
      <div className="relative h-[420px] w-full overflow-hidden bg-[#bfd4c4]">
        {event.mainImage?.asset?.url ? (
          <img
            loading="lazy"
            src={event.mainImage.asset.url}
            alt={event.title}
            className="h-full w-full object-cover transition duration-700 group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-[radial-gradient(circle_at_top,_rgba(214,232,92,0.9),_transparent_55%),linear-gradient(135deg,_#bfd4c4,_#f7f7f2)] text-sm font-bold uppercase tracking-[0.24em] text-[#45695a]">
            Jisedai Event
          </div>
        )}

        <div className="absolute inset-0 bg-gradient-to-t from-[#111712]/85 via-[#111712]/20 to-transparent transition duration-300 group-hover:opacity-0" />
        <div className="absolute inset-0 bg-[#45695a]/95 opacity-0 transition duration-300 group-hover:opacity-100" />

        <div className="absolute left-4 top-4 rounded-full border border-white/30 bg-[#f7f7f2]/90 px-3 py-1 text-xs font-semibold text-[#45695a] backdrop-blur">
          {eventDate.short}
        </div>

        <div className="absolute inset-x-0 bottom-0 p-6">
          <div className="transition duration-300 group-hover:translate-y-0 md:translate-y-24">
            <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.24em] text-[#d6e85c]">
              Event
            </p>

            <h2 className="font-serif text-3xl leading-tight tracking-[-0.035em] text-white">
              {event.title}
            </h2>

            <div className="mt-4 hidden space-y-2 text-sm text-white/85 md:block md:opacity-0 md:transition md:duration-300 md:group-hover:opacity-100">
              <div className="flex items-center gap-2">
                <CalendarDays size={15} className="text-[#d6e85c]" />
                <span>{eventDate.full}</span>
              </div>

              <div className="flex items-center gap-2">
                <MapPin size={15} className="text-[#d6e85c]" />
                <span>{event.location}</span>
              </div>

              <p className="line-clamp-3 pt-2 text-sm leading-7 text-white/90">
                {event.description}
              </p>

              <Link
                to="/"
                state={{
                  eventInterest: event.title,
                  eventMessage,
                }}
                className="inline-flex items-center gap-2 pt-2 text-sm font-bold text-[#d6e85c] transition hover:text-white"
              >
                {event.ctaLabel || "Register Interest"}
                <span aria-hidden="true">&rarr;</span>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-[#111712]/10 p-6 md:hidden">
        <div className="mb-4 flex flex-col gap-2 text-sm text-[#4d5951]">
          <div className="flex items-center gap-2">
            <CalendarDays size={15} className="text-[#45695a]" />
            <span>{eventDate.full}</span>
          </div>

          <div className="flex items-center gap-2">
            <MapPin size={15} className="text-[#45695a]" />
            <span>{event.location}</span>
          </div>
        </div>

        <p
          className={`text-sm leading-7 text-[#4d5951] ${
            isExpanded ? "" : "line-clamp-3"
          }`}
        >
          {event.description}
        </p>

        {event.description && event.description.length > 140 && (
          <button
            type="button"
            onClick={() => setIsExpanded((value) => !value)}
            className="mt-2 text-sm font-bold text-[#45695a] transition hover:text-[#111712]"
          >
            {isExpanded ? "See less" : "See more"}
          </button>
        )}

        <div className="mt-6">
          <Link
            to="/"
            state={{
              eventInterest: event.title,
              eventMessage,
            }}
            className="inline-flex items-center gap-2 text-sm font-bold text-[#45695a] transition hover:text-[#111712]"
          >
            {event.ctaLabel || "Register Interest"}
            <span aria-hidden="true">&rarr;</span>
          </Link>
        </div>
      </div>
    </article>
  );
}

export default function Events() {
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [dateOrder, setDateOrder] = useState("newest-posted");

  useEffect(() => {
    setIsLoading(true);
    setErrorMessage("");

    client
      .fetch(`*[_type == "event"]{
        _id,
        _createdAt,
        title,
        eventDate,
        location,
        description,
        mainImage{
          asset->{url}
        },
        ctaLabel,
        interestMessage
      }`)
      .then((data) => {
        setEvents(data);
      })
      .catch((error) => {
        console.error(error);
        setErrorMessage(
          error?.message ||
            "We could not load events right now. Please confirm that your Sanity events are published and publicly readable."
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const sortedEvents = useMemo(() => {
    return [...events].sort((first, second) => {
      const firstDate = new Date(first.eventDate || 0).getTime();
      const secondDate = new Date(second.eventDate || 0).getTime();
      const firstPosted = new Date(first._createdAt || 0).getTime();
      const secondPosted = new Date(second._createdAt || 0).getTime();

      if (dateOrder === "newest-posted") return secondPosted - firstPosted;
      if (dateOrder === "oldest-posted") return firstPosted - secondPosted;
      return dateOrder === "soonest-event" ? firstDate - secondDate : secondDate - firstDate;
    });
  }, [events, dateOrder]);

  useEffect(() => {
    setCurrentPage(1);
  }, [dateOrder]);

  const totalPages = Math.max(1, Math.ceil(sortedEvents.length / EVENTS_PER_PAGE));
  const paginatedEvents = sortedEvents.slice(
    (currentPage - 1) * EVENTS_PER_PAGE,
    currentPage * EVENTS_PER_PAGE
  );

  return (
    <section className="relative min-h-screen overflow-hidden bg-[#eeeee8] px-5 py-28 text-[#111712] sm:px-8 md:px-12">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute left-1/4 top-10 h-64 w-64 rounded-full bg-[#bfd4c4]/45 blur-3xl" />
        <div className="absolute bottom-10 right-1/4 h-72 w-72 rounded-full bg-[#d6e85c]/30 blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-7xl">
        <div className="max-w-4xl">
          <p className="mb-4 text-[11px] font-bold uppercase tracking-[0.24em] text-[#45695a]">
            In person and online
          </p>
          <h1 className="font-serif text-5xl leading-[0.92] tracking-[-0.06em] text-[#111712] md:text-7xl">
            Learn, build, and meet the people moving technology forward.
          </h1>

          <p className="mt-6 max-w-2xl text-base leading-8 text-[#4d5951] md:text-lg">
            Workshops, bootcamps, and community sessions designed to help
            people learn, apply, and grow with AI.
          </p>
        </div>

        {isLoading ? (
          <p className="mt-16 text-[#4d5951]">Loading events...</p>
        ) : errorMessage ? (
          <div className="mx-auto mt-16 max-w-3xl rounded-3xl border border-red-500/20 bg-red-500/10 px-6 py-5 text-center text-red-100">
            <p className="font-semibold">Events could not be loaded.</p>
            <p className="mt-2 text-sm text-red-100/80">{errorMessage}</p>
          </div>
        ) : !events.length ? (
          <div className="mt-16 max-w-3xl rounded-3xl border border-[#111712]/15 bg-white/50 px-6 py-6 text-[#4d5951]">
            <p className="font-semibold text-[#45695a]">
              No upcoming events are listed yet.
            </p>
            <p className="mt-2 text-sm text-[#4d5951]">
              Add and publish Event documents in Sanity Studio to show them
              here automatically.
            </p>
          </div>
        ) : (
          <>
            <div className="mt-12 flex">
              <label className="sr-only" htmlFor="event-date-order">Sort events by date</label>
              <select
                id="event-date-order"
                value={dateOrder}
                onChange={(event) => setDateOrder(event.target.value)}
                className="rounded-2xl border border-[#111712]/15 bg-white/70 px-5 py-4 text-sm font-semibold text-[#344039] focus:outline-none focus:border-[#45695a]/60"
              >
                <option value="newest-posted">Latest event posted first</option>
                <option value="oldest-posted">Oldest event posted first</option>
                <option value="soonest-event">Soonest event date first</option>
                <option value="latest-event">Latest event date first</option>
              </select>
            </div>
            <div className="mt-16 grid gap-8 md:grid-cols-2 xl:grid-cols-3">
              {paginatedEvents.map((event) => (
                <EventCard key={event._id} event={event} />
              ))}
            </div>

            {totalPages > 1 && (
              <div className="mt-14 flex flex-wrap justify-center gap-3">
                {Array.from({ length: totalPages }, (_, index) => {
                  const page = index + 1;
                  return (
                    <button
                      key={page}
                      type="button"
                      onClick={() => setCurrentPage(page)}
                      className={`h-11 min-w-11 rounded-full border px-4 text-sm font-semibold transition ${
                        currentPage === page
                            ? "border-[#111712] bg-[#111712] text-[#f5f5ef]"
                            : "border-[#111712]/15 text-[#45695a] hover:border-[#45695a]/60"
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}
